import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  findFirstPremiumLapsedOwnedCompanyId,
  getCompaniesForUser,
  userHasAccessibleWorkspace,
} from '../services/companyService';
import { MembershipBlockedScreen } from './MembershipBlockedScreen';

const REFRESH_MS = 45_000;

export function MembershipAccessGate({
  uid,
  children,
}: {
  uid: string;
  children: ReactNode;
}) {
  const [checking, setChecking] = useState(true);
  const [ownerLapsedCompanyId, setOwnerLapsedCompanyId] = useState<string | null>(null);
  const [allWorkspacesOnHold, setAllWorkspacesOnHold] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function refresh() {
      try {
        const [lapsedOwnedId, companies] = await Promise.all([
          findFirstPremiumLapsedOwnedCompanyId(uid),
          getCompaniesForUser(uid),
        ]);
        if (cancelled) return;
        setOwnerLapsedCompanyId(lapsedOwnedId);
        setAllWorkspacesOnHold(
          companies.length > 0 && !userHasAccessibleWorkspace(companies),
        );
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    void refresh();
    const id = window.setInterval(() => void refresh(), REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [uid]);

  if (checking) {
    return (
      <div className="app-loading-screen">
        <span style={{ color: 'var(--ph-muted)' }}>Loading…</span>
      </div>
    );
  }

  if (ownerLapsedCompanyId) {
    return <MembershipBlockedScreen mode="owner" />;
  }

  if (allWorkspacesOnHold) {
    return <MembershipBlockedScreen mode="member" />;
  }

  return <>{children}</>;
}
